import type { ReactNode } from "react";
import type { Permission, UserRole } from "@/types";

export type NavGroupId =
  "overview" | "management" | "operations" | "finance" | "account";

export type NavItemDef = {
  id: string;
  labelKey: string;
  /** Sidebar section; children inherit the parent's group. */
  group?: NavGroupId;
  path: string;
  icon: ReactNode;
  /** If set, the link is shown only when the user has this permission (or any of them). */
  permission?: Permission | readonly Permission[];
  hideForRoles?: UserRole[];
  /** If true, show profile incomplete badge (partner + sales agent). */
  settingsProfileBadge?: boolean;
  /**
   * Exact path match only (no prefix). Use for index routes that share a
   * prefix with sibling subpages (e.g. `/devices` vs `/devices/carousel`).
   */
  exactPath?: boolean;
  /** When false, omit from the sidebar but keep in Cmd/Ctrl+K search. Default true. */
  showInSidebar?: boolean;
  children?: NavItemDef[];
};

export type NavItem = Omit<NavItemDef, "children" | "labelKey"> & {
  label: string;
  children?: NavItem[];
};

export type BreadcrumbCrumb = { id: string; label: string; path?: string };

/** Ids hidden from a host who has no activities yet (nothing to book or manage). */
const HOST_ACTIVITY_NAV_IDS: readonly string[] = ["bookings", "activities"];

export function navItemVisible(
  item: Pick<NavItemDef, "permission" | "hideForRoles">,
  userRole: UserRole,
  hasPermission: (permission: Permission) => boolean,
): boolean {
  if (item.hideForRoles?.includes(userRole)) {
    return false;
  }
  if (item.permission === undefined) {
    return true;
  }
  const required = Array.isArray(item.permission)
    ? item.permission
    : [item.permission];
  return required.some((permission) => hasPermission(permission));
}

export function isNavPathActive(
  pathname: string,
  path: string,
  exactPath = false,
): boolean {
  if (path === "/") {
    return pathname === "/";
  }
  if (exactPath) {
    return pathname === path;
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Nav tree the user may see: drops hidden items and empty sections, and points
 * a section at its first sidebar child.
 */
export function filterNavItems(
  items: NavItem[],
  role: UserRole,
  hasPermission: (permission: Permission) => boolean,
  hostHasNoActivities: boolean,
): NavItem[] {
  const visible: NavItem[] = [];
  for (const item of items) {
    if (hostHasNoActivities && HOST_ACTIVITY_NAV_IDS.includes(item.id)) {
      continue;
    }
    if (item.children && item.children.length > 0) {
      const children = item.children.filter((child) =>
        navItemVisible(child, role, hasPermission),
      );
      if (children.length === 0) {
        continue;
      }
      const sidebarChildren = children.filter(
        (child) => child.showInSidebar !== false,
      );
      visible.push({
        ...item,
        path: (sidebarChildren[0] ?? children[0]).path,
        children,
      });
      continue;
    }
    if (!navItemVisible(item, role, hasPermission)) {
      continue;
    }
    visible.push(item);
  }
  return visible;
}

/** Section → page trail for the top bar, derived from the visible nav tree. */
export function findActiveTrail(
  items: NavItem[],
  pathname: string,
  groupLabel: (group: NavGroupId) => string,
): BreadcrumbCrumb[] {
  for (const item of items) {
    const children = item.children ?? [];
    const groupCrumb: BreadcrumbCrumb[] =
      item.group && item.group !== "overview"
        ? [{ id: `group-${item.group}`, label: groupLabel(item.group) }]
        : [];
    const activeChild = children
      .filter(
        (child) =>
          child.showInSidebar !== false &&
          isNavPathActive(pathname, child.path, child.exactPath),
      )
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (activeChild) {
      return [
        { id: item.id, label: item.label, path: item.path },
        {
          id: activeChild.id,
          label: activeChild.label,
          path: activeChild.path,
        },
      ];
    }
    if (
      children.length === 0 &&
      isNavPathActive(pathname, item.path, item.exactPath)
    ) {
      return [
        ...groupCrumb,
        { id: item.id, label: item.label, path: item.path },
      ];
    }
  }
  return [];
}
